import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { musicServiceImagesByImporterId } from '../../common/musicServiceImages';
import { integrationTypeToImporterId } from '../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
import { useSMLTheme } from '../../music24/theme/useSMLTheme';
import { toggleService } from './presaveCreateFacts';
import { presaveCreateMessages } from './presaveCreateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveServiceChipsProps {
  selected: string[];
  onChange: (services: string[]) => void;
  disabled?: boolean;
}

/** The height of a logo lockup in a chip; the files are about twice as wide as high. */
const LOGO_HEIGHT = 40;

function serviceName(service: string): string {
  return musicServicesByMusicAPIID[service]?.name ?? service;
}

interface ServiceChipProps {
  service: string;
  isOn: boolean;
  disabled?: boolean;
  onToggle: (service: string) => void;
}

/** One service: its logo lockup (the public pre-save page shows the same ones) and a tick. */
const ServiceChip: React.FC<ServiceChipProps> = ({ service, isOn, disabled, onToggle }) => {
  const intl = useIntl();
  const { theme } = useSMLTheme();
  const importerId = integrationTypeToImporterId[service];
  const images = importerId === undefined ? undefined : musicServiceImagesByImporterId[importerId];
  const Logo = images === undefined ? undefined : theme === 'dark' ? images.logoColor.dark : images.logoColor.light;
  const name = serviceName(service);
  const onClick = useCallback(() => onToggle(service), [onToggle, service]);
  return (
    <button
      type="button"
      className={clsx(styles.svc, isOn ? styles.svcOn : styles.svcOff)}
      aria-pressed={isOn}
      aria-label={intl.formatMessage(isOn ? presaveCreateMessages.serviceOn : presaveCreateMessages.serviceOff, {
        service: name,
      })}
      disabled={disabled}
      onClick={onClick}
      data-testid={`presave-service-${service}`}
    >
      <span className={styles.svcLogo}>
        {Logo === undefined ? name : <Logo height={LOGO_HEIGHT} width={LOGO_HEIGHT * 2} aria-hidden="true" />}
      </span>
      <span className={styles.svcTick}>
        <FontAwesomeIcon icon={faCheck} />
      </span>
    </button>
  );
};

/**
 * "Fans pre-save on" (design presave-create a1, a2): the five services musicapi can save a track
 * into, all on by default. A tap turns one off; the last one stays on.
 */
const PresaveServiceChipsComponent: React.FC<PresaveServiceChipsProps> = ({ selected, onChange, disabled }) => {
  const intl = useIntl();
  const onToggle = useCallback(
    (service: string) => onChange(toggleService(selected, service, defaultPresaveServices)),
    [onChange, selected]
  );
  return (
    <div className={styles.field}>
      <div className={styles.svcHead}>
        <span className={styles.svcLabel}>{intl.formatMessage(presaveCreateMessages.servicesLabel)}</span>
        <span className={styles.svcCount}>
          {intl.formatMessage(presaveCreateMessages.servicesCount, {
            on: selected.length,
            total: defaultPresaveServices.length,
          })}
        </span>
      </div>
      <div className={styles.svcGrid}>
        {defaultPresaveServices.map((service) => (
          <ServiceChip
            key={service}
            service={service}
            isOn={selected.includes(service)}
            disabled={disabled}
            onToggle={onToggle}
          />
        ))}
      </div>
      <span className={styles.hint}>{intl.formatMessage(presaveCreateMessages.servicesHint)}</span>
    </div>
  );
};

export const PresaveServiceChips = TypedMemo(PresaveServiceChipsComponent);
